import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, User } from "lucide-react";
import { useState, useEffect } from "react";

export default function Nav() {
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onDark = location.pathname === "/" && !scrolled;

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[var(--bone)]/85 border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-5">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/logo.png" alt="Irene Educational and Developmental Foundation" className="w-11 h-11 object-contain" />
          <div className="leading-tight">
            <div className={`font-serif text-base md:text-lg leading-[1.05] max-w-[220px] ${onDark ? "text-[var(--bone)]" : "text-[var(--ink)]"}`}>Irene Educational and Developmental Foundation</div>
            <div className={`overline mt-1 ${onDark ? "text-[var(--bone)]/70" : "text-[var(--muted)]"}`}>Rebuild · Educate</div>
          </div>
        </Link>

        <nav className={`hidden md:flex items-center gap-8 text-sm ${onDark ? "text-[var(--bone)]" : "text-[var(--ink)]"}`}>
          <a href="/#mission" data-testid="nav-mission" className="hover:text-[var(--terracotta)] transition-colors">Mission</a>
          <a href="/#programs" data-testid="nav-programs" className="hover:text-[var(--terracotta)] transition-colors">Programs</a>
          <a href="/#goals" data-testid="nav-goals" className="hover:text-[var(--terracotta)] transition-colors">Goals</a>
          <Link to="/membership" data-testid="nav-membership" className="hover:text-[var(--terracotta)] transition-colors">Membership</Link>
          <Link to="/volunteer" data-testid="nav-volunteer" className="hover:text-[var(--terracotta)] transition-colors">Volunteer</Link>
          <Link to="/partner" data-testid="nav-partner" className="hover:text-[var(--terracotta)] transition-colors">Collaborate</Link>
          <a href="/#contact" data-testid="nav-contact" className="hover:text-[var(--terracotta)] transition-colors">Contact</a>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className={`text-sm ${onDark ? "text-[var(--bone)]" : "text-[var(--ink)]"} hover:text-[var(--terracotta)]`}>
                  Admin
                </Link>
              )}
              <div className={`hidden sm:flex items-center gap-2 text-sm ${onDark ? "text-[var(--bone)]" : "text-[var(--ink)]"}`}>
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span className="truncate max-w-[120px]">{user.name?.split(" ")[0]}</span>
              </div>
              <button data-testid="nav-logout" onClick={logout} className={`btn-secondary ${onDark ? "!text-[var(--bone)] !border-[var(--bone)]/50 hover:!bg-[var(--bone)] hover:!text-[var(--forest)]" : ""}`}>
                <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          ) : (
            <button data-testid="nav-login" onClick={login} className={`text-sm ${onDark ? "text-[var(--bone)]" : "text-[var(--ink)]"} hover:text-[var(--terracotta)]`}>
              Sign in
            </button>
          )}
          <Link to="/donate" data-testid="nav-donate" className="btn-primary !py-2.5 !px-5 text-sm relative">
            Donate
            <span className="hidden sm:inline ml-1 text-[10px] font-mono uppercase tracking-wider opacity-90">soon</span>
          </Link>
          <a
            href="https://www.facebook.com/reel/232805032778074"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-facebook"
            aria-label="Facebook"
            className={`hidden md:grid place-items-center w-9 h-9 border transition-colors ${
              onDark
                ? "border-[var(--bone)]/30 text-[var(--bone)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]"
                : "border-black/15 text-[var(--ink)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)]"
            }`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06C2 17.08 5.66 21.24 10.44 22v-7.03H7.9v-2.91h2.54V9.83c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.91h-2.33V22C18.34 21.24 22 17.08 22 12.06z"/></svg>
          </a>
        </div>
      </div>
    </header>
  );
}
